import { formatDateTime, formatDuration } from '@kominiarz/shared';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { useConfirm } from '../../components/Confirm';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { useToast } from '../../components/Toast';
import { visitTitle } from '../../components/VisitStatus';
import { errorMessage } from '../../lib/api';
import { useLayoutMode } from '../../lib/breakpoints';
import {
  CALENDAR_VIEWS,
  VIEW_LABELS,
  local,
  periodTitle,
  shiftDate,
  todayLocal,
  viewRange,
  type CalendarView,
} from '../../lib/calendarDates';
import { readPref, writePref } from '../../lib/storage';
import { useUserOptions } from '../../lib/usersApi';
import { useUpdateVisit, useVisits } from '../../lib/visitsApi';
import { LoadBoundary } from '../../components/LoadBoundary';
import { PushPrompt } from '../../components/push/PushPrompt';
import { AgendaView } from './AgendaView';
import type { MoveRequest } from './FullCalendarView';
import styles from './calendar.module.css';

// FullCalendar is loaded only when a grid view is opened (the phone default is the list).
const FullCalendarView = lazy(() => import('./FullCalendarView').then((m) => ({ default: m.FullCalendarView })));

/** A week grid is unreadable at phone width; the list covers the coming days instead. */
const PHONE_VIEWS: CalendarView[] = ['lista', 'dzien', 'miesiac'];

const isDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v) && local(v).isValid;

export function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = useLayoutMode();
  const user = useCurrentUser();
  const users = useUserOptions();
  const confirm = useConfirm();
  const toast = useToast();
  const updateVisit = useUpdateVisit();

  const views = mode === 'phone' ? PHONE_VIEWS : CALENDAR_VIEWS;
  const requested = (params.get('widok') ?? readPref('calendar.view')) as CalendarView | null;
  const view: CalendarView = requested && views.includes(requested) ? requested : mode === 'phone' ? 'lista' : 'tydzien';
  const date = isDate(params.get('data')) ? params.get('data')! : todayLocal();
  const filter = params.get('filtr') ?? readPref('calendar.filter') ?? 'all';

  const range = useMemo(() => viewRange(view, date), [view, date]);
  const visits = useVisits(range, filter);

  useEffect(() => writePref('calendar.view', view), [view]);
  useEffect(() => writePref('calendar.filter', filter), [filter]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const filterOptions = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'mine', label: 'Moje' },
    { value: 'team', label: 'Zespołowe' },
    ...(user.role === 'admin'
      ? (users.data ?? []).filter((u) => u.id !== user.id).map((u) => ({ value: `user:${u.id}`, label: u.name }))
      : []),
  ];

  async function handleMove({ visit, startAt, durationMinutes, revert }: MoveRequest) {
    const ok = await confirm({
      title: 'Przenieść wizytę?',
      message: (
        <>
          <strong>{visitTitle(visit)}</strong>
          {visit.address ? `, ${visit.address}` : ''}
          <br />
          Nowy termin: {formatDateTime(startAt)} ({formatDuration(durationMinutes)})
        </>
      ),
      confirmLabel: 'Przenieś',
    });
    if (!ok) {
      revert();
      return;
    }
    try {
      await updateVisit.mutateAsync({ id: visit.id, body: { startAt, durationMinutes } });
      toast.success('Termin zmieniony');
    } catch (err) {
      revert();
      toast.error(errorMessage(err));
    }
  }

  const isToday = date === todayLocal();

  const todayButton = (
    <Button variant="secondary" onClick={() => update({ data: null })} aria-pressed={isToday}>
      Dziś
    </Button>
  );

  return (
    <>
      <PageHeader title="Kalendarz" actions={mode === 'phone' ? todayButton : undefined} />
      <PageBody>
        <PushPrompt />
        <div className={styles.toolbar}>
          <div className={styles.period}>
            {mode !== 'phone' && todayButton}
            <Button
              variant="secondary"
              className={styles.arrow}
              icon={<ChevronLeft aria-hidden="true" />}
              onClick={() => update({ data: shiftDate(view, date, -1) })}
              aria-label="Poprzedni okres"
            >
              <span className="visually-hidden">Wstecz</span>
            </Button>
            <h2 className={styles.periodTitle} aria-live="polite">
              {periodTitle(view, date)}
            </h2>
            <Button
              variant="secondary"
              className={styles.arrow}
              icon={<ChevronRight aria-hidden="true" />}
              onClick={() => update({ data: shiftDate(view, date, 1) })}
              aria-label="Następny okres"
            >
              <span className="visually-hidden">Dalej</span>
            </Button>
          </div>
          <div className={styles.controls}>
            <div className={styles.views} role="group" aria-label="Widok kalendarza">
              {views.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={v === view ? `${styles.viewButton} ${styles.viewActive}` : styles.viewButton}
                  aria-pressed={v === view}
                  onClick={() => update({ widok: v })}
                >
                  {VIEW_LABELS[v]}
                </button>
              ))}
            </div>
            <label className={styles.filter}>
              <span className="visually-hidden">Czyje wizyty pokazać</span>
              <select value={filter} onChange={(e) => update({ filtr: e.target.value })}>
                {filterOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" className={styles.filterIcon} />
            </label>
          </div>
        </div>

        {visits.isError && <Alert kind="error">{errorMessage(visits.error)}</Alert>}
        {visits.isPending ? (
          <Spinner />
        ) : view === 'lista' ? (
          <AgendaView range={range} visits={visits.data ?? []} />
        ) : (
          <LoadBoundary
            fallback={
              <Alert kind="warning" title="Nie udało się wczytać siatki kalendarza">
                Sprawdź połączenie z internetem i odśwież stronę. Wizyty zobaczysz też w widoku „Lista”.
              </Alert>
            }
          >
            <Suspense fallback={<Spinner />}>
              <FullCalendarView
                view={view}
                date={date}
                visits={visits.data ?? []}
                onOpenVisit={(id) => navigate(`/wizyty/${id}`)}
                onNewAt={(d, t) => navigate(`/wizyty/nowa?data=${d}&godzina=${t}`)}
                onOpenDay={(d) => update({ widok: 'dzien', data: d })}
                onMove={handleMove}
              />
            </Suspense>
          </LoadBoundary>
        )}
      </PageBody>
    </>
  );
}
